import { FocusEvent, KeyboardEvent, useCallback, useRef } from "react";

import { fieldPathOf } from "./fieldPath";

/**
 * Tabbing off a flagged field answers it, the way pressing Keep would.
 *
 * A reviewer working down a form with the keyboard reads each field, decides it
 * is fine, and moves on. Making them press a chip to say so turns "this is
 * right" into a second gesture on every field that has anything to say — which
 * on a poor scan is most of them, and which is what makes a queue feel like a
 * ritual rather than a read.
 *
 * Nothing is written. Keeping never was an edit: the value does not change, the
 * flag stops asking, and the count comes down.
 */

/**
 * Only a tab counts.
 *
 * A pointer leaving the field is not the same gesture. A reviewer clicks the
 * document pane to look closer at the very field they are unsure about, and
 * treating that as agreement would answer the question they clicked away to
 * think about. Tabbing carries the intent that clicking does not — the same
 * distinction `useSelectOnKeyboardFocus` draws, for the same reason.
 */
export function useKeepOnTabAway({
  answerKeyForPath,
  acknowledge,
}: {
  /** The key that answers the field at this path, or undefined if nothing asks. */
  answerKeyForPath: (path: string) => string | undefined;
  acknowledge: (key: string) => void;
}): {
  onKeyDownCapture: (event: KeyboardEvent<HTMLElement>) => void;
  onBlurCapture: (event: FocusEvent<HTMLElement>) => void;
} {
  // Set by the keypress that causes the blur, read by the blur itself. A ref
  // because nothing renders from it, and cleared on the blur it explains so a
  // tab that moves focus out of the form entirely cannot leak into a later one.
  const tabbing = useRef(false);

  const onKeyDownCapture = useCallback((event: KeyboardEvent<HTMLElement>) => {
    tabbing.current = event.key === "Tab";
  }, []);

  const onBlurCapture = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const wasTab = tabbing.current;
      tabbing.current = false;
      if (!wasTab) return;

      const path = fieldPathOf(event.target);
      if (path == null) return;

      const key = answerKeyForPath(path);
      if (key == null) return;
      acknowledge(key);
    },
    [answerKeyForPath, acknowledge]
  );

  return { onKeyDownCapture, onBlurCapture };
}
